import { useQuery } from '@tanstack/react-query';

import { callService } from '@/services/api';
import { queryKeys } from '@/services/query.config';

import type { CaseItem } from '../data/types';

export type PayerHoursCase = Pick<
  CaseItem,
  'organizationId' | 'programId' | 'payerSlug' | 'workflowType'
>;

/**
 * The working hours of a case's payer for its workflow type (BV-2525), for the
 * clock popover on the Workforce Console's case card and bulk rows. The cases
 * come from FindCaseRecords, which does not carry payer hours, so they are
 * fetched when a popover opens (the hook lives in the popover body). Keyed by
 * (org, program, payer, workflow type), so every case with the same payer and
 * workflow type shares one request; payer hours change rarely, so it stays
 * fresh, and cached between popovers, for 30 minutes.
 */
export const usePayerHours = (caseItem: PayerHoursCase) => {
  const { organizationId = '', programId = '', payerSlug = '', workflowType = '' } = caseItem;
  return useQuery({
    queryKey: queryKeys.workforce.payerHours(organizationId, programId, payerSlug, workflowType),
    queryFn: () =>
      callService.getPayerHours({
        organization_id: organizationId,
        program_id: programId,
        // getApiPath substitutes path params verbatim; slugs such as "ss&c" need escaping.
        payer_slug: encodeURIComponent(payerSlug),
        workflow_type: workflowType,
      }),
    enabled: !!organizationId && !!programId && !!payerSlug,
    staleTime: 30 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};
